import {
  Checkbox,
  CondensedButton,
  Flex,
  Heading,
  Text,
} from "@instructure/ui";
import DOMPurify from "dompurify";
import { useTranslation } from "react-i18next";
import React, { useContext, useEffect, useState } from "react";
import { AttainmentRuleService } from "../../services/AttainmentRuleService";
import { sort } from "../../utils/utils";
import { IssuersContext } from "../../views/CurriculumMap";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { SINGLE, Selector } from "../Selectors/Selector";
import AddBadgeModal from "./AddBadgeModal";

const Badging = ({
  modalData,
  selectedBadge,
  setSelectedBadge,
  applyBadge,
  setApplyBadge,
  readOnly,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const issuers = useContext(IssuersContext);
  const [badgeOptions, setBadgeOptions] = useState([]);
  const [selectedIssuer, setSelectedIssuer] = useState(null);
  const [clearBadge, setClearBadge] = useState(false);
  const [openBadgeModal, setOpenBadgeModal] = useState(false);
  const [initialBadges, setInitialBadges] = useState([]);

  // Fetch badge classes if mapping has a mapped badge
  useEffect(() => {
    if (modalData.badgeMapping?.issuerOpenBadgeId && issuers.length > 0) {
      const issuer = issuers.find(
        (issuer) =>
          issuer.openBadgeId === modalData.badgeMapping.issuerOpenBadgeId
      );
      AttainmentRuleService.getBadgeClassesByIssuer(issuer.entityId)
        .then((badges) => {
          badges.forEach((badge) => {
            appendBadgeItemData(badge);
          });
          setInitialBadges(sortBadgesByName(badges));
        })
        .catch(() => {
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("badging.badgeClasses"),
            }),
            ERROR
          );
        });
    }
  }, [modalData.badgeMapping?.issuerOpenBadgeId, issuers, showNotification]);

  // If badge mapping exists, set issuer
  useEffect(() => {
    if (modalData.badgeMapping && issuers.length > 0) {
      setSelectedIssuer(
        issuers.find(
          (issuer) =>
            issuer.openBadgeId === modalData.badgeMapping.issuerOpenBadgeId
        )
      );
    }
  }, [issuers, modalData.badgeMapping]);

  // If badge mapping exists, set badge class
  useEffect(() => {
    if (modalData.badgeMapping && initialBadges.length > 0) {
      setBadgeOptions(initialBadges);
      setSelectedBadge(
        initialBadges.find(
          (b) => b.openBadgeId === modalData.badgeMapping.openBadgeId
        )
      );
    }
  }, [initialBadges, modalData.badgeMapping, setSelectedBadge]);

  const sortBadgesByName = (badges) => {
    return sort(badges, (badge) => badge.name);
  };

  const onBadgeCreated = (badge) => {
    if (badge) {
      const newBadgeOptions = [...badgeOptions];
      newBadgeOptions.push(appendBadgeItemData(badge));
      setBadgeOptions(sortBadgesByName(newBadgeOptions));
    }
  };

  const appendBadgeItemData = (badge) => {
    badge.id = badge.entityId;
    badge.label = badge.name;
    const sanitizedImage = DOMPurify.sanitize(badge.image);
    badge.renderBeforeLabel = (
      <img src={sanitizedImage} width="25px" height="25px" alt={badge.name} />
    );
    return badge;
  };

  const handleRewardCheckBox = (e) => {
    if (!e.target.checked) {
      setSelectedIssuer(null);
      setBadgeOptions([]);
      setSelectedBadge(null);
    }
    setApplyBadge(e.target.checked);
  };

  const handleSelectIssuer = (entityId) => {
    setSelectedIssuer(issuers.find((issuer) => issuer.entityId === entityId));
    setClearBadge(true);
    AttainmentRuleService.getBadgeClassesByIssuer(entityId)
      .then((badges) => {
        badges.forEach((badge) => {
          badge.id = badge.entityId;
          badge.label = badge.name;
          const sanitizedImage = DOMPurify.sanitize(badge.image);
          badge.renderBeforeLabel = (
            <img
              src={sanitizedImage}
              width="25px"
              height="25px"
              alt={badge.name}
            />
          );
        });
        setBadgeOptions(sortBadgesByName(badges));
      })
      .catch(() => {
        showNotification(
          t("notifications.general.fetchError", {
            obj: t("badging.badgeClasses"),
          }),
          ERROR
        );
      });
  };

  useEffect(() => {
    if (clearBadge) {
      setSelectedBadge(null);
      setClearBadge(false);
    }
  }, [clearBadge, setSelectedBadge]);

  return (
    <>
      <Flex wrap="wrap">
        <Flex.Item width="100%" margin="0 0 small 0">
          <Heading as="h2" level="h4" border="bottom">
            {t("badging.badging")}
          </Heading>
        </Flex.Item>
        {readOnly ? (
          selectedBadge ? (
            <Flex.Item width="50%">
              <Flex wrap="wrap" gap="small">
                <Flex.Item width="100%">
                  <Text weight="bold">
                    {t("badging.issuer")}: {selectedIssuer.name}
                  </Text>
                </Flex.Item>
                <Flex.Item width="100%">
                  <Text weight="bold">
                    {t("badging.badge")}: {selectedBadge.name}
                  </Text>
                </Flex.Item>
              </Flex>
            </Flex.Item>
          ) : (
            <Text>{t("badging.noBadge")}</Text>
          )
        ) : (
          <Flex.Item width="50%">
            <Flex wrap="wrap" gap="small">
              <Flex.Item width="100%">
                <Checkbox
                  size="small"
                  label={t("badging.rewardBadge")}
                  checked={applyBadge}
                  onChange={handleRewardCheckBox}
                />
              </Flex.Item>
              {applyBadge && (
                <>
                  <Flex.Item width="100%">
                    <Selector
                      type={SINGLE}
                      id="issuer-select"
                      label={t("badging.selectIssuer")}
                      options={issuers}
                      selectHandler={handleSelectIssuer}
                      size={"small"}
                      selectedItemId={selectedIssuer?.id}
                    />
                  </Flex.Item>
                  <Flex.Item width="100%">
                    <Selector
                      type={SINGLE}
                      id="badge-select"
                      label={
                        <Flex justifyItems="space-between">
                          <Flex.Item>{t("badging.selectBadge")}</Flex.Item>
                          <Flex.Item>
                            <CondensedButton
                              size="small"
                              onClick={() => setOpenBadgeModal(true)}
                              interaction={
                                badgeOptions.length > 0 ? "enabled" : "disabled"
                              }
                            >
                              {t("badging.addNewBadge")}
                            </CondensedButton>
                          </Flex.Item>
                        </Flex>
                      }
                      options={badgeOptions}
                      selectHandler={(badgeId) =>
                        setSelectedBadge(
                          badgeOptions.find((b) => b.id === badgeId)
                        )
                      }
                      size="small"
                      interaction={badgeOptions.length > 0}
                      selectedItemId={selectedBadge?.id}
                      clear={clearBadge}
                    />
                  </Flex.Item>
                </>
              )}
              {/* Future Feature */}
              {/* {applyBadge && (
              <Flex.Item margin="small 0 0 0">
                <RadioInputGroup
                  size="small"
                  name="badgeMasteryFor"
                  defaultValue="generalMastery"
                  description={<Text size="small">Reward Badge for:</Text>}
                >
                  <RadioInput
                    value="generalMastery"
                    label={"General Mastery"}
                  />
                  {attainmentLevel && (
                    <RadioInput
                      value="masteryAtAttainment"
                      label={`Mastery at Attainment Level: ${attainmentLevel}`}
                    />
                  )}
                </RadioInputGroup>
              </Flex.Item>
            )} */}
            </Flex>
          </Flex.Item>
        )}
        {applyBadge && (
          <Flex.Item textAlign="center" shouldGrow width="50%">
            {selectedBadge && (
              <img
                src={selectedBadge.image}
                alt={selectedBadge.name}
                style={{ width: 150, height: 150 }}
              />
            )}
          </Flex.Item>
        )}
      </Flex>
      <AddBadgeModal
        openBadgeModal={openBadgeModal}
        setOpenBadgeModal={setOpenBadgeModal}
        issuer={selectedIssuer}
        onBadgeCreated={onBadgeCreated}
      />
    </>
  );
};

export default Badging;
